import { useEffect, useRef, useState } from 'react'

/** The part of an upload scope the gate drives. */
export interface GatedUploadScope<UploadArgs extends unknown[]> {
  seed: (filenames: string[]) => void
  upload: (...args: UploadArgs) => void
  remove: (id: string) => void
  /**
   * Whether the client has uploaded or deleted in this area, kept by the scope's provider so it outlives
   * the area: an upload that settles after the page was left is then still recorded on return. Without
   * it the gate falls back to its own state.
   */
  touched?: boolean
  markTouched?: () => void
}

const EMPTY: readonly string[] = []

const sameStrings = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index])

/**
 * Records an upload area's filenames in the configuration once the client has uploaded or deleted there.
 * Until then the stored filenames win: they are seeded into the area once on mount, and nothing is
 * written, so a resume or a regenerated session cannot overwrite them. The write is skipped while the
 * filenames equal the stored ones, because unrelated upload activity re-renders every area.
 *
 * @example
 * const exterior = useGatedUploadScope(scope, scope.filenames, value.exteriorViewFilenames,
 *   (filenames) => patch({ exteriorViewFilenames: filenames }))
 */
export function useGatedUploadScope<UploadArgs extends unknown[]>(
  scope: GatedUploadScope<UploadArgs>,
  filenames: readonly string[],
  stored: readonly string[] | undefined,
  write: (filenames: string[]) => void
) {
  const [ownTouched, setOwnTouched] = useState(false)
  const touched = scope.touched ?? ownTouched
  const markTouched = () => {
    setOwnTouched(true)
    scope.markTouched?.()
  }
  const storedAtMount = useRef(stored ?? EMPTY)
  const current = stored ?? EMPTY

  const seeded = useRef(false)
  const { seed } = scope
  useEffect(() => {
    if (seeded.current) return
    seeded.current = true
    if (storedAtMount.current.length) seed([...storedAtMount.current])
  }, [seed])

  useEffect(() => {
    if (!touched || sameStrings(filenames, current)) return
    write([...filenames])
  }, [touched, filenames, current, write])

  return {
    touched,
    hadRestored: storedAtMount.current.length > 0,
    upload: (...args: UploadArgs) => {
      markTouched()
      scope.upload(...args)
    },
    remove: (id: string) => {
      markTouched()
      scope.remove(id)
    },
  }
}
